// @ts-check
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import { adminProductTrustPillarsService } from '../services/adminProductExtrasService';
import adminSiteSettingsService from '../services/adminSiteSettingsService';

/**
 * Lets an admin attach "extra" trust pillars to a specific product, beyond the
 * auto-matched fabric pillar and the always-shown universal ones. Picks from the full
 * catalog configured in Settings → Home Page → Trust Pillars — this component doesn't
 * edit pillar content, only which ones are attached to this product and in what order.
 *
 * @param {{ productId: string }} props
 */
const ProductTrustPillarsManager = ({ productId }) => {
  const toast = useToast();
  const [catalog, setCatalog] = useState(/** @type {Array<any>} */ ([]));
  const [picks, setPicks] = useState(/** @type {Array<{ pillar_id: string, display_order: number }>} */ ([]));
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [catalogSetting, currentPicks] = await Promise.allSettled([
        adminSiteSettingsService.getSetting('trust_pillars_config'),
        adminProductTrustPillarsService.getTrustPillars(productId),
      ]);

      if (catalogSetting.status === 'fulfilled') {
        try {
          const parsed = JSON.parse(catalogSetting.value.value);
          setCatalog(Array.isArray(parsed.pillars) ? parsed.pillars : []);
        } catch {
          setCatalog([]);
        }
      } else {
        setCatalog([]);
      }

      if (currentPicks.status === 'fulfilled') setPicks(currentPicks.value);
      else toast.error('Failed to load trust pillars');
    } finally {
      setLoading(false);
    }
  }, [productId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const catalogById = Object.fromEntries(catalog.map((p) => [p.id, p]));
  const pickedIds = new Set(picks.map((p) => p.pillar_id));
  const available = catalog.filter((p) => !pickedIds.has(p.id));

  const handleAdd = async (pillarId) => {
    try {
      await adminProductTrustPillarsService.addTrustPillar(productId, pillarId);
      await load();
    } catch (err) {
      toast.error(err.message || 'Failed to attach pillar');
    }
  };

  const handleRemove = async (pillarId) => {
    const prev = [...picks];
    setPicks(picks.filter((p) => p.pillar_id !== pillarId));
    try {
      await adminProductTrustPillarsService.deleteTrustPillar(productId, pillarId);
    } catch (err) {
      toast.error('Failed to remove pillar');
      setPicks(prev);
    }
  };

  const handleReorder = async (pillarId, direction) => {
    const idx = picks.findIndex((p) => p.pillar_id === pillarId);
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= picks.length) return;

    const updated = [...picks];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    setPicks(updated);

    try {
      await adminProductTrustPillarsService.reorderTrustPillars(
        productId,
        updated.map((p) => p.pillar_id),
      );
    } catch (err) {
      toast.error('Failed to reorder pillars');
      load();
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading trust pillars...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground/70">
          Extra pillars shown on this product's page, in addition to its auto-matched fabric pillar and the universal
          pillars every product shows. Edit pillar content and add new pillars under Settings → Home Page → Trust
          Pillars.
        </p>
        <button
          type="button"
          onClick={() => setShowPicker(!showPicker)}
          className="flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-1.5 rounded-lg text-xs hover:bg-primary/90 transition flex-shrink-0"
        >
          <Plus size={14} />
          Add Pillar
        </button>
      </div>

      {showPicker && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-1 max-h-60 overflow-y-auto">
          {available.length === 0 ? (
            <p className="text-xs text-muted-foreground/70 text-center py-2">
              {catalog.length === 0 ? 'No trust pillars configured yet.' : 'All catalog pillars are already attached.'}
            </p>
          ) : (
            available.map((pillar) => (
              <button
                type="button"
                key={pillar.id}
                onClick={() => handleAdd(pillar.id)}
                className="w-full flex items-center justify-between gap-3 p-2 rounded-lg hover:bg-muted/50 transition text-left"
              >
                <div>
                  <p className="text-sm font-medium">{pillar.label}</p>
                  <p className="text-xs text-muted-foreground">{pillar.short}</p>
                </div>
                <Plus size={16} className="flex-shrink-0 text-muted-foreground/70" />
              </button>
            ))
          )}
        </div>
      )}

      {picks.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-card border border-border rounded-lg">
          No extra pillars attached — this product shows only its auto-matched fabric pillar and the universal pillars.
        </div>
      ) : (
        <div className="space-y-2">
          {picks.map((pick, index) => {
            const pillar = catalogById[pick.pillar_id];
            return (
              <div key={pick.pillar_id} className="flex items-center gap-4 p-3 border border-border rounded-lg bg-card">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{pillar?.label || pick.pillar_id}</p>
                  {!pillar && (
                    <p className="text-xs text-amber-500">No longer in the catalog — will be dropped on next edit.</p>
                  )}
                  {pillar && <p className="text-xs text-muted-foreground truncate">{pillar.short}</p>}
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <div className="flex flex-col gap-0.5">
                    <button
                      type="button"
                      onClick={() => handleReorder(pick.pillar_id, 'up')}
                      disabled={index === 0}
                      className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                      title="Move up"
                    >
                      <ArrowUp size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleReorder(pick.pillar_id, 'down')}
                      disabled={index === picks.length - 1}
                      className="p-1.5 rounded-lg hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition"
                      title="Move down"
                    >
                      <ArrowDown size={16} />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemove(pick.pillar_id)}
                    className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/60 text-red-500 transition"
                    title="Remove"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductTrustPillarsManager;
